import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loading, Title, useDataProvider, useNotify } from 'react-admin';
import { Alert, Box, Button, Card, CardContent, Chip, Grid, Tab, Tabs, TextField, Typography, } from '@mui/material';
import {
    Android as AndroidIcon,
    Apple as AppleIcon,
    CheckCircle as CheckCircleIcon,
    CloudUpload as CloudUploadIcon,
    Upload as UploadIcon,
} from '@mui/icons-material';
import type { ClientSecrets, Client } from '../../types';
import type { LogtoDataProvider } from "../../dataProvider.ts";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`secrets-tabpanel-${index}`}
            aria-labelledby={`secrets-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export const SecretUpload = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const navigate = useNavigate();
    const dataProvider = useDataProvider<LogtoDataProvider>();
    const notify = useNotify();

    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [uploadingAction, setUploadingAction] = useState<string | null>(null);
    const uploading = uploadingAction !== null;
    const [secrets, setSecrets] = useState<ClientSecrets | null>(null);
    const [client, setClient] = useState<Client | null>(null);

    // Android state
    const [androidKeystore, setAndroidKeystore] = useState<File | null>(null);
    const [androidKeystorePassword, setAndroidKeystorePassword] = useState('');
    const [androidKeyAlias, setAndroidKeyAlias] = useState('');
    const [androidKeyPassword, setAndroidKeyPassword] = useState('');

    // iOS state
    const [iosCertificate, setIosCertificate] = useState<File | null>(null);
    const [iosProvisioningProfile, setIosProvisioningProfile] = useState<File | null>(null);
    const [iosExtensionProfile, setIosExtensionProfile] = useState<File | null>(null);
    const [iosCertificatePassword, setIosCertificatePassword] = useState('');

    // Google Play state
    const [googlePlayServiceAccount, setGooglePlayServiceAccount] = useState<File | null>(null);

    // App Store Connect state
    const [appStoreKeyFile, setAppStoreKeyFile] = useState<File | null>(null);
    const [appStoreKeyId, setAppStoreKeyId] = useState('');
    const [appStoreIssuerId, setAppStoreIssuerId] = useState('');

    // App versioning state
    const [editingVersion, setEditingVersion] = useState(false);
    const [appVersion, setAppVersion] = useState('');
    const [lastBuildNumber, setLastBuildNumber] = useState('');

    // iOS Team Configuration state (Feature: 008-add-team-name)
    const [teamId, setTeamId] = useState('');
    const [teamName, setTeamName] = useState('');
    const [itcTeamId, setItcTeamId] = useState('');
    const [itcTeamName, setItcTeamName] = useState('');

    useEffect(() => {
        loadSecrets();
    }, [clientId]);

    const useSharedIOS = client?.useSharedIosAccount ?? false;
    const useSharedAndroid = client?.useSharedAndroidAccount ?? false;

    const loadSecrets = async () => {
        if (!clientId) return;

        setLoading(true);
        try {
            const [secretsResponse, clientResponse] = await Promise.all([
                dataProvider.getOne('secrets', { id: clientId }),
                dataProvider.getOne<Client>('clients', { id: clientId }),
            ]);
            const secretsData = secretsResponse.data as ClientSecrets;
            setSecrets(secretsData);
            setClient(clientResponse.data);

            // Load team configuration into state (Feature: 008-add-team-name)
            if (secretsData.teamId) setTeamId(secretsData.teamId);
            if (secretsData.teamName) setTeamName(secretsData.teamName);
            if (secretsData.itcTeamId) setItcTeamId(secretsData.itcTeamId);
            if (secretsData.itcTeamName) setItcTeamName(secretsData.itcTeamName);
            setAppVersion(secretsData.appVersion || '1.0.0');
            setLastBuildNumber(String(secretsData.lastBuildNumber || 0));
        } catch (error) {
            console.error('Failed to load secrets:', error);
            // Secrets might not exist yet, that's OK
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTeamConfig = async () => {
        if (!clientId) return;

        if (!teamId || !itcTeamId) {
            notify('Team ID and App Store Connect Team ID are required', { type: 'warning' });
            return;
        }

        setUploadingAction('team');
        try {
            // Update secrets via dataProvider
            await dataProvider.update('secrets', {
                id: clientId,
                data: {
                    teamId,
                    teamName: teamName || null,
                    itcTeamId,
                    itcTeamName: itcTeamName || null,
                },
                previousData: secrets || {}
            });
            notify('iOS team configuration updated successfully', { type: 'success' });
            await loadSecrets();
        } catch (error: any) {
            notify(error?.message || 'Failed to update iOS team configuration', { type: 'error' });
        } finally {
            setUploadingAction(null);
        }
    };

    const handleUploadAndroid = async () => {
        if (!clientId || !androidKeystore || !androidKeystorePassword || !androidKeyAlias || !androidKeyPassword) {
            notify('Please fill all Android keystore fields', { type: 'warning' });
            return;
        }

        setUploadingAction('android');
        try {
            await dataProvider.uploadAndroidKeystore(clientId, {
                keystore: androidKeystore,
                keystore_password: androidKeystorePassword,
                key_alias: androidKeyAlias,
                key_password: androidKeyPassword,
            });
            notify('Android keystore uploaded successfully', { type: 'success' });
            await loadSecrets();
            // Clear form
            setAndroidKeystore(null);
            setAndroidKeystorePassword('');
            setAndroidKeyAlias('');
            setAndroidKeyPassword('');
        } catch (error: any) {
            notify(error?.message || 'Failed to upload Android keystore', { type: 'error' });
        } finally {
            setUploadingAction(null);
        }
    };

    const handleUploadIOSCertificate = async () => {
        if (!clientId || !iosCertificate || !iosCertificatePassword) {
            notify('Please select certificate file and enter password', { type: 'warning' });
            return;
        }

        setUploadingAction('ios-cert');
        try {
            await dataProvider.uploadIOSCertificate(clientId, {
                certificate: iosCertificate,
                certificate_password: iosCertificatePassword,
            });
            notify('iOS certificate uploaded successfully', { type: 'success' });
            await loadSecrets();
            setIosCertificate(null);
            setIosCertificatePassword('');
        } catch (error: any) {
            notify(error?.message || 'Failed to upload iOS certificate', { type: 'error' });
        } finally {
            setUploadingAction(null);
        }
    };

    const handleUploadProvisioningProfile = async () => {
        if (!clientId || !iosProvisioningProfile) {
            notify('Please select provisioning profile file', { type: 'warning' });
            return;
        }

        setUploadingAction('provisioning');
        try {
            await dataProvider.uploadIOSProvisioningProfile(clientId, {
                provisioning_profile: iosProvisioningProfile,
            });
            notify('Provisioning profile uploaded successfully', { type: 'success' });
            await loadSecrets();
            setIosProvisioningProfile(null);
        } catch (error: any) {
            notify(error?.message || 'Failed to upload provisioning profile', { type: 'error' });
        } finally {
            setUploadingAction(null);
        }
    };

    const handleUploadExtensionProfile = async () => {
        if (!clientId || !iosExtensionProfile) {
            notify('Please select extension profile file', { type: 'warning' });
            return;
        }

        setUploadingAction('extension');
        try {
            await dataProvider.uploadIOSExtensionProfile(clientId, {
                extension_profile: iosExtensionProfile,
            });
            notify('Extension profile uploaded successfully', { type: 'success' });
            await loadSecrets();
            setIosExtensionProfile(null);
        } catch (error: any) {
            notify(error?.message || 'Failed to upload extension profile', { type: 'error' });
        } finally {
            setUploadingAction(null);
        }
    };

    const handleUploadGooglePlay = async () => {
        if (!clientId || !googlePlayServiceAccount) {
            notify('Please select Google Play service account file', { type: 'warning' });
            return;
        }

        setUploadingAction('google-play');
        try {
            await dataProvider.uploadGooglePlayServiceAccount(clientId, {
                service_account: googlePlayServiceAccount,
            });
            notify('Google Play service account uploaded successfully', { type: 'success' });
            await loadSecrets();
            setGooglePlayServiceAccount(null);
        } catch (error: any) {
            notify(error?.message || 'Failed to upload Google Play service account', { type: 'error' });
        } finally {
            setUploadingAction(null);
        }
    };

    const handleUploadAppStore = async () => {
        if (!clientId || !appStoreKeyFile || !appStoreKeyId || !appStoreIssuerId) {
            notify('Please fill all App Store Connect fields', { type: 'warning' });
            return;
        }

        setUploadingAction('app-store');
        try {
            await dataProvider.uploadAppStoreConnectKey(clientId, {
                key_file: appStoreKeyFile,
                key_id: appStoreKeyId,
                issuer_id: appStoreIssuerId,
            });
            notify('App Store Connect key uploaded successfully', { type: 'success' });
            await loadSecrets();
            setAppStoreKeyFile(null);
            setAppStoreKeyId('');
            setAppStoreIssuerId('');
        } catch (error: any) {
            notify(error?.message || 'Failed to upload App Store Connect key', { type: 'error' });
        } finally {
            setUploadingAction(null);
        }
    };

    const handleSaveVersion = async () => {
        if (!clientId) return;

        setUploadingAction('version');
        try {
            await dataProvider.update('secrets', {
                id: clientId,
                data: {
                    appVersion,
                    lastBuildNumber: parseInt(lastBuildNumber) || 0,
                },
                previousData: secrets,
            });
            notify('Version updated successfully', { type: 'success' });
            setEditingVersion(false);
            await loadSecrets();
        } catch (error: any) {
            notify(error?.message || 'Failed to update version', { type: 'error' });
        } finally {
            setUploadingAction(null);
        }
    };

    if (loading) {
        return <Loading/>;
    }

    return (
        <Box p={2}>
            <Title title={`Manage Secrets - ${client?.name || clientId}`}/>

            {/* Secrets Status Card */}
            {secrets && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Secrets Status</Typography>
                            <Chip
                                label="Secrets"
                                color="primary"
                                size="small"
                            />
                        </Box>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AndroidIcon color={secrets.hasAndroidKeystore ? 'success' : 'disabled'}/>
                                    <Typography variant="body2">
                                        Android Keystore: {secrets.hasAndroidKeystore ? '✓' : '✗'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AppleIcon color={(secrets.hasIosCertificate || useSharedIOS) ? 'success' : 'disabled'}/>
                                    <Typography variant="body2">
                                        iOS Certificate: {(secrets.hasIosCertificate || useSharedIOS) ? (useSharedIOS ? 'Shared' : '✓') : '✗'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AppleIcon color={(secrets.hasProvisioningProfile && secrets.hasExtensionProfile) ? 'success' : secrets.hasProvisioningProfile ? 'warning' : 'disabled'}/>
                                    <Typography variant="body2">
                                        Profiles: {secrets.hasProvisioningProfile ? '✓' : '✗'} / Ext: {secrets.hasExtensionProfile ? '✓' : '✗'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AppleIcon color={((secrets.teamId && secrets.itcTeamId) || useSharedIOS) ? 'success' : 'disabled'}/>
                                    <Typography variant="body2">
                                        iOS Team Config: {((secrets.teamId && secrets.itcTeamId) || useSharedIOS) ? (useSharedIOS ? 'Shared' : '✓') : '✗'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CloudUploadIcon
                                        color={(secrets.hasPlayStoreCredentials || useSharedAndroid) ? 'success' : 'disabled'}/>
                                    <Typography variant="body2">
                                        Google Play: {(secrets.hasPlayStoreCredentials || useSharedAndroid) ? (useSharedAndroid ? 'Shared' : '✓') : '✗'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AppleIcon color={(secrets.hasAppStoreConnectKey || useSharedIOS) ? 'success' : 'disabled'}/>
                                    <Typography variant="body2">
                                        App Store: {(secrets.hasAppStoreConnectKey || useSharedIOS) ? (useSharedIOS ? 'Shared' : '✓') : '✗'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* App Version */}
            {secrets && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6">App Versioning</Typography>
                            {!editingVersion && (
                                <Button size="small" onClick={() => setEditingVersion(true)}>
                                    Edit
                                </Button>
                            )}
                        </Box>
                        {editingVersion ? (
                            <Grid container spacing={2} alignItems="center">
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        label="Marketing Version"
                                        value={appVersion}
                                        onChange={(e) => setAppVersion(e.target.value)}
                                        fullWidth
                                        size="small"
                                        helperText="e.g. 2.9.7"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        label="Last Build Number"
                                        value={lastBuildNumber}
                                        onChange={(e) => setLastBuildNumber(e.target.value)}
                                        fullWidth
                                        size="small"
                                        type="number"
                                        helperText="Last published build number"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Button variant="contained" size="small" onClick={handleSaveVersion} disabled={uploading} sx={{ mr: 1 }}>
                                        Save
                                    </Button>
                                    <Button size="small" onClick={() => setEditingVersion(false)}>
                                        Cancel
                                    </Button>
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">Marketing Version</Typography>
                                    <Typography variant="h6">{secrets.appVersion || '1.0.0'}</Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="body2" color="text.secondary">Last Build Number</Typography>
                                    <Typography variant="h6">{secrets.lastBuildNumber || 0}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Next build will use #{(secrets.lastBuildNumber || 0) + 1}
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Upload Tabs */}
            <Card>
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        Upload Secrets
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        All secrets are stored securely in the database.
                    </Alert>

                    <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}
                          aria-label="secret upload tabs">
                        <Tab icon={<AndroidIcon/>} label="Android" iconPosition="start"/>
                        <Tab icon={<AppleIcon/>} label="iOS Certificate" iconPosition="start"/>
                        <Tab icon={<AppleIcon/>} label="Provisioning Profile" iconPosition="start"/>
                        <Tab icon={<AppleIcon/>} label="iOS Team" iconPosition="start"/>
                        <Tab icon={<CloudUploadIcon/>} label="Google Play" iconPosition="start"/>
                        <Tab icon={<AppleIcon/>} label="App Store" iconPosition="start"/>
                    </Tabs>

                    {/* Android Tab */}
                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <input
                                    accept=".jks,.keystore"
                                    style={{ display: 'none' }}
                                    id="android-keystore-file"
                                    type="file"
                                    onChange={(e) => e.target.files && setAndroidKeystore(e.target.files[0])}
                                />
                                <label htmlFor="android-keystore-file">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon/>}
                                        fullWidth
                                    >
                                        Select Keystore File (.jks, .keystore)
                                    </Button>
                                </label>
                                {androidKeystore && (
                                    <Chip
                                        label={androidKeystore.name}
                                        onDelete={() => setAndroidKeystore(null)}
                                        sx={{ mt: 1 }}
                                        icon={<CheckCircleIcon/>}
                                        color="success"
                                    />
                                )}
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    type="password"
                                    label="Keystore Password"
                                    value={androidKeystorePassword}
                                    onChange={(e) => setAndroidKeystorePassword(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Key Alias"
                                    value={androidKeyAlias}
                                    onChange={(e) => setAndroidKeyAlias(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    type="password"
                                    label="Key Password"
                                    value={androidKeyPassword}
                                    onChange={(e) => setAndroidKeyPassword(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUploadAndroid}
                                    disabled={uploading || !androidKeystore}
                                    startIcon={<UploadIcon/>}
                                >
                                    {uploadingAction === 'android' ? 'Uploading...' : 'Upload Android Keystore'}
                                </Button>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* iOS Certificate Tab */}
                    <TabPanel value={tabValue} index={1}>
                        {useSharedIOS ? (
                            <Alert severity="success">
                                Using shared iOS certificate. Per-client upload not required.
                            </Alert>
                        ) : (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <input
                                    accept=".p12,.pfx"
                                    style={{ display: 'none' }}
                                    id="ios-certificate-file"
                                    type="file"
                                    onChange={(e) => e.target.files && setIosCertificate(e.target.files[0])}
                                />
                                <label htmlFor="ios-certificate-file">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon/>}
                                        fullWidth
                                    >
                                        Select Certificate (.p12)
                                    </Button>
                                </label>
                                {iosCertificate && (
                                    <Chip
                                        label={iosCertificate.name}
                                        onDelete={() => setIosCertificate(null)}
                                        sx={{ mt: 1 }}
                                        icon={<CheckCircleIcon/>}
                                        color="success"
                                    />
                                )}
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    type="password"
                                    label="Certificate Password"
                                    value={iosCertificatePassword}
                                    onChange={(e) => setIosCertificatePassword(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUploadIOSCertificate}
                                    disabled={uploading || !iosCertificate}
                                    startIcon={<UploadIcon/>}
                                >
                                    {uploadingAction === 'ios-cert' ? 'Uploading...' : 'Upload iOS Certificate'}
                                </Button>
                            </Grid>
                        </Grid>
                        )}
                    </TabPanel>

                    {/* Provisioning Profile Tab */}
                    <TabPanel value={tabValue} index={2}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    Always required per-client — tied to specific Bundle ID.
                                </Alert>
                                <input
                                    accept=".mobileprovision"
                                    style={{ display: 'none' }}
                                    id="ios-profile-file"
                                    type="file"
                                    onChange={(e) => e.target.files && setIosProvisioningProfile(e.target.files[0])}
                                />
                                <label htmlFor="ios-profile-file">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon/>}
                                        fullWidth
                                    >
                                        Select Provisioning Profile (.mobileprovision)
                                    </Button>
                                </label>
                                {iosProvisioningProfile && (
                                    <Chip
                                        label={iosProvisioningProfile.name}
                                        onDelete={() => setIosProvisioningProfile(null)}
                                        sx={{ mt: 1 }}
                                        icon={<CheckCircleIcon/>}
                                        color="success"
                                    />
                                )}
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUploadProvisioningProfile}
                                    disabled={uploading || !iosProvisioningProfile}
                                    startIcon={<UploadIcon/>}
                                >
                                    {uploadingAction === 'provisioning' ? 'Uploading...' : 'Upload Provisioning Profile'}
                                </Button>
                            </Grid>

                            {/* Extension Profile */}
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                                    Extension Profile (OneSignal Notifications)
                                </Typography>
                                <input
                                    accept=".mobileprovision"
                                    style={{ display: 'none' }}
                                    id="ios-extension-profile-file"
                                    type="file"
                                    onChange={(e) => e.target.files && setIosExtensionProfile(e.target.files[0])}
                                />
                                <label htmlFor="ios-extension-profile-file">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon/>}
                                        fullWidth
                                    >
                                        Select Extension Profile (.mobileprovision)
                                    </Button>
                                </label>
                                {iosExtensionProfile && (
                                    <Chip
                                        label={iosExtensionProfile.name}
                                        onDelete={() => setIosExtensionProfile(null)}
                                        sx={{ mt: 1 }}
                                        icon={<CheckCircleIcon/>}
                                        color="success"
                                    />
                                )}
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUploadExtensionProfile}
                                    disabled={uploading || !iosExtensionProfile}
                                    startIcon={<UploadIcon/>}
                                >
                                    {uploadingAction === 'extension' ? 'Uploading...' : 'Upload Extension Profile'}
                                </Button>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* iOS Team Configuration Tab */}
                    <TabPanel value={tabValue} index={3}>
                        {useSharedIOS ? (
                            <Alert severity="success">
                                Using shared iOS team configuration. Per-client setup not required.
                            </Alert>
                        ) : (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Configure your Apple Developer and App Store Connect team information. These values are required for Fastlane to manage certificates and app submissions.
                                </Alert>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Apple Developer Team ID"
                                    value={teamId}
                                    onChange={(e) => setTeamId(e.target.value)}
                                    required
                                    placeholder="ABCDE12345"
                                    helperText="10-character team ID from Apple Developer Portal"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Apple Developer Team Name"
                                    value={teamName}
                                    onChange={(e) => setTeamName(e.target.value)}
                                    placeholder="Your Company Name (optional)"
                                    helperText="Optional display name for your team"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="App Store Connect Team ID"
                                    value={itcTeamId}
                                    onChange={(e) => setItcTeamId(e.target.value)}
                                    required
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                    helperText="Team ID from App Store Connect"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="App Store Connect Team Name"
                                    value={itcTeamName}
                                    onChange={(e) => setItcTeamName(e.target.value)}
                                    placeholder="Your Company Name (optional)"
                                    helperText="Optional display name for App Store Connect team"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUpdateTeamConfig}
                                    disabled={uploading || !teamId || !itcTeamId}
                                    startIcon={<CheckCircleIcon/>}
                                >
                                    {uploadingAction === 'team' ? 'Updating...' : 'Update Team Configuration'}
                                </Button>
                            </Grid>
                        </Grid>
                        )}
                    </TabPanel>

                    {/* Google Play Tab */}
                    <TabPanel value={tabValue} index={4}>
                        {useSharedAndroid ? (
                            <Alert severity="success">
                                Using shared Google Play service account. Per-client upload not required.
                            </Alert>
                        ) : (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <input
                                    accept=".json"
                                    style={{ display: 'none' }}
                                    id="google-play-file"
                                    type="file"
                                    onChange={(e) => e.target.files && setGooglePlayServiceAccount(e.target.files[0])}
                                />
                                <label htmlFor="google-play-file">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon/>}
                                        fullWidth
                                    >
                                        Select Service Account JSON
                                    </Button>
                                </label>
                                {googlePlayServiceAccount && (
                                    <Chip
                                        label={googlePlayServiceAccount.name}
                                        onDelete={() => setGooglePlayServiceAccount(null)}
                                        sx={{ mt: 1 }}
                                        icon={<CheckCircleIcon/>}
                                        color="success"
                                    />
                                )}
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Alert severity="info">
                                    Upload the JSON key file for your Google Play Service Account.
                                    You can download this from Google Cloud Console.
                                </Alert>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUploadGooglePlay}
                                    disabled={uploading || !googlePlayServiceAccount}
                                    startIcon={<UploadIcon/>}
                                >
                                    {uploadingAction === 'google-play' ? 'Uploading...' : 'Upload Google Play Credentials'}
                                </Button>
                            </Grid>
                        </Grid>
                        )}
                    </TabPanel>

                    {/* App Store Connect Tab */}
                    <TabPanel value={tabValue} index={5}>
                        {useSharedIOS ? (
                            <Alert severity="success">
                                Using shared App Store Connect key. Per-client upload not required.
                            </Alert>
                        ) : (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <input
                                    accept=".p8"
                                    style={{ display: 'none' }}
                                    id="app-store-key-file"
                                    type="file"
                                    onChange={(e) => e.target.files && setAppStoreKeyFile(e.target.files[0])}
                                />
                                <label htmlFor="app-store-key-file">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon/>}
                                        fullWidth
                                    >
                                        Select API Key (.p8)
                                    </Button>
                                </label>
                                {appStoreKeyFile && (
                                    <Chip
                                        label={appStoreKeyFile.name}
                                        onDelete={() => setAppStoreKeyFile(null)}
                                        sx={{ mt: 1 }}
                                        icon={<CheckCircleIcon/>}
                                        color="success"
                                    />
                                )}
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Key ID"
                                    value={appStoreKeyId}
                                    onChange={(e) => setAppStoreKeyId(e.target.value)}
                                    required
                                    placeholder="XXXXXXXXXX"
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Issuer ID"
                                    value={appStoreIssuerId}
                                    onChange={(e) => setAppStoreIssuerId(e.target.value)}
                                    required
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Alert severity="info">
                                    You can find the Key ID and Issuer ID in App Store Connect under Users and Access →
                                    Keys.
                                </Alert>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleUploadAppStore}
                                    disabled={uploading || !appStoreKeyFile}
                                    startIcon={<UploadIcon/>}
                                >
                                    {uploadingAction === 'app-store' ? 'Uploading...' : 'Upload App Store Connect Key'}
                                </Button>
                            </Grid>
                        </Grid>
                        )}
                    </TabPanel>

                    <Box sx={{ mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(`/clients/${clientId}/show`)}
                        >
                            Back to Client
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};
